<script setup lang="ts">
  import { AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger } from 'radix-vue';

  const props = defineProps<{
    value: string;
    triggerClass?: string;
    contentClass?: string;
  }>();

  const emit = defineEmits<{
    stateChange: [value: string, isOpen: boolean];
  }>();

  // Handle state changes from radix-vue
  const handleStateChange = (open: boolean) => {
    emit('stateChange', props.value, open);
  };
</script>

<template>
  <AccordionItem :value="value" class="AccordionItem" @update:open="handleStateChange">
    <AccordionHeader class="AccordionHeader">
      <AccordionTrigger class="AccordionTrigger" :class="triggerClass">
        <div class="flex-1">
          <slot name="trigger" />
        </div>
        <slot name="chevron">
          <iconify-icon icon="fa6-solid:chevron-down" class="trigger-chevron" />
        </slot>
      </AccordionTrigger>
    </AccordionHeader>
    <AccordionContent :class="contentClass" class="AccordionContent">
      <slot name="content" />
    </AccordionContent>
  </AccordionItem>
</template>

<style lang="scss" scoped>
  @import '@/assets/styles/variables';
  .AccordionTrigger {
    font-size: 1rem;
    width: 100%;
    display: flex;
    justify-content: space-between;
    text-align: left;
  }

  .AccordionHeader {
    display: grid;
    gap: 1rem;
  }
  .trigger-chevron {
    font-size: 1.5rem;
    padding: 0.25rem;
    color: $neutral-gray;
    transition: all ease 0.3s;
  }

  .AccordionItem {
    &[data-state='open'] {
      .trigger-chevron {
        transform: rotate(180deg);
      }
    }
  }
  .AccordionContent[data-state='open'] {
    animation: slideDown 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }
  .AccordionContent[data-state='closed'] {
    opacity: 0;
    animation: slideUp 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }

  .AccordionChevron {
    transition: transform 300ms cubic-bezier(0.87, 0, 0.13, 1);
  }
  .AccordionTrigger[data-state='open'] > .AccordionChevron {
    transform: rotate(180deg);
  }

  @keyframes slideDown {
    from {
      height: 0;
    }
    to {
      height: var(--radix-accordion-content-height);
    }
  }

  @keyframes slideUp {
    from {
      height: var(--radix-accordion-content-height);
    }
    to {
      height: 0;
      opacity: 0;
    }
  }
</style>
